import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  AppBadge,
  AppFilterChipAction,
  AppDropdownMenu,
  AppDropdownMenuTrigger,
  AppDropdownMenuContent,
  AppDropdownMenuLabel,
  AppDropdownMenuCheckboxItem,
} from '@easyllama/ui-components';
import { LlamaCursorDown } from '@easyllama/ui-components/icons';
import { MAX_ROWS_PER_GROUP, MAX_VISIBLE_TYPE_PILLS, SURFACE_TOKENS, TYPE_ORDER } from '../constants';

function TypeFilterBar({ groups, selectedTypes, onToggleType, onSelectAll, showPillSkeleton }) {
  if (showPillSkeleton) {
    const pillWidths = ['w-20', 'w-24', 'w-24', 'w-20'];
    return (
      <div className="flex items-center gap-2" aria-hidden="true" data-testid="global-search-pill-skeleton">
        {pillWidths.map((widthClass, index) => (
          <div key={index} className={clsx('h-(--gs-pill-height) animate-pulse rounded-full bg-llama-grey-3', widthClass)} />
        ))}
      </div>
    );
  }

  if (groups.length === 0) return null;

  const counts = groups.map((group) => ({ ...group, count: Math.min(group.rows.length, MAX_ROWS_PER_GROUP) }));
  const totalCount = counts.reduce((sum, group) => sum + group.count, 0);

  const sortedByHits = [...counts].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return TYPE_ORDER.indexOf(a.key) - TYPE_ORDER.indexOf(b.key);
  });
  const visiblePillGroups = sortedByHits.slice(0, MAX_VISIBLE_TYPE_PILLS);
  const visibleKeys = new Set(visiblePillGroups.map((group) => group.key));
  const overflowGroups = counts.filter((group) => !visibleKeys.has(group.key));
  const hiddenSelectedCount = overflowGroups.filter((group) => selectedTypes?.has(group.key)).length;
  const isAllActive = selectedTypes === null;

  // !important overrides AppFilterChipAction's default el:-prefixed sizing/padding classes.
  const pillClassName = '!h-(--gs-pill-height) !rounded-full !px-3 !py-0 !text-[length:var(--gs-subtitle-font)]';
  // The active pill's count badge has its own white-on-translucent spec, not AppBadge's default "active" colors.
  const activeCountBadgeClassName = '!rounded-full !bg-white/25 !px-1.5 !py-px !text-white';
  const restCountBadgeClassName = '!rounded-full !bg-llama-grey-9 !px-1.5 !py-px !text-llama-grey-5';
  // "+N more"'s rest state is filled grey, distinct from every other pill's white rest state.
  const overflowTriggerRestClassName = '!h-(--gs-pill-height) !gap-1.5 !rounded-full !border !border-llama-grey-5 !bg-llama-grey-9 !px-3 !py-0 !text-[length:var(--gs-subtitle-font)] !font-bold !text-llama-grey-10';

  return (
    <div className="flex items-center gap-2">
      <AppFilterChipAction
        variant="suggestion"
        size="small"
        active={isAllActive}
        className={clsx(pillClassName, !isAllActive && '!text-llama-grey-10 hover:!bg-llama-grey-9')}
        onClick={onSelectAll}
      >
        All{' '}
        <AppBadge
          type="count"
          count={totalCount}
          state={isAllActive ? 'active' : 'default'}
          className={isAllActive ? activeCountBadgeClassName : restCountBadgeClassName}
        />
      </AppFilterChipAction>
      {visiblePillGroups.map((group) => {
        const isActive = selectedTypes?.has(group.key) ?? false;
        return (
          <AppFilterChipAction
            key={group.key}
            variant="suggestion"
            size="small"
            className={clsx(pillClassName, !isActive && '!text-llama-grey-10 hover:!bg-llama-grey-9')}
            active={isActive}
            onClick={() => onToggleType(group.key)}
          >
            {group.label}{' '}
            <AppBadge
              type="count"
              count={group.count}
              state={isActive ? 'active' : 'default'}
              className={isActive ? activeCountBadgeClassName : restCountBadgeClassName}
            />
          </AppFilterChipAction>
        );
      })}
      {overflowGroups.length > 0 && (
        <AppDropdownMenu>
          <AppDropdownMenuTrigger asChild>
            <AppFilterChipAction
              variant="suggestion"
              size="small"
              className={hiddenSelectedCount > 0 ? pillClassName : overflowTriggerRestClassName}
              active={hiddenSelectedCount > 0}
            >
              +{overflowGroups.length} more{hiddenSelectedCount > 0 ? ` · ${hiddenSelectedCount} on` : ''}
              <LlamaCursorDown className="ml-1 size-4" />
            </AppFilterChipAction>
          </AppDropdownMenuTrigger>
          {/* Portals to a sibling of the dialog's own z-[1020] surface, so it needs the same override to render above it. */}
          <AppDropdownMenuContent align="start" className="!z-[1020] !p-1" style={SURFACE_TOKENS}>
            <AppDropdownMenuLabel className="!px-2 !pt-1.5 !pb-1 !text-[length:var(--gs-label-font)] !font-bold !tracking-(--gs-label-tracking) !text-[#6f6f6f]">MORE TYPES</AppDropdownMenuLabel>
            {overflowGroups.map((group) => (
              // Portals outside the dialog's reset scope, so legacy CSS wins without !important.
              <AppDropdownMenuCheckboxItem
                key={group.key}
                checked={selectedTypes?.has(group.key) ?? false}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={() => onToggleType(group.key)}
                count={group.count}
                className="!gap-2 !py-1.5 !pl-8 !pr-2"
              >
                {group.label}
              </AppDropdownMenuCheckboxItem>
            ))}
          </AppDropdownMenuContent>
        </AppDropdownMenu>
      )}
    </div>
  );
}

TypeFilterBar.propTypes = {
  groups: PropTypes.array.isRequired,
  selectedTypes: PropTypes.instanceOf(Set),
  onToggleType: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  showPillSkeleton: PropTypes.bool.isRequired,
};

export default TypeFilterBar;
